/**
 * Shared DOCX → case import logic for ImportCaseModal, ParseDraftToCaseModal,
 * and Uploads → Import Existing Practice bulk onboarding.
 */
import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import {
  buildImportTabs,
  importDocxFile,
  type ImportDocxResult,
  type ImportTabDraft,
} from "./docxImport";
import {
  DEFAULT_LINE_SPACING,
  DEFAULT_PARAGRAPH_SPACING_PT,
} from "./editor-defaults";
import { createDraftDocument, type DocumentTab } from "./documentContentService";
import { privatePracticeCaseFields } from "./organizations";
import { extractCaseMetadataFromPlainText } from "./ai";
import {
  getAllMatterTypeIds,
  inferCategoryFromMatterType,
  resolveMatterTypeId,
  type CaseCategoryLabel,
} from "./caseTaxonomyRegistry";
import { resolveStartingCaseStage } from "./caseStages";
import {
  buildMatterTypeUpdateFromPicker,
  type MatterTypePickerValue,
} from "./matterTypeChange";
import { db } from "./firebase";
import { isDeleted } from "./trash";

export const OLD_CASE_IMPORT_SOURCE = "old_case_import";

export type OldCaseImportStatus =
  | "processing"
  | "review"
  | "case_created"
  | "error";

export interface ExtractedCaseParty {
  name: string;
  role: string;
}

export interface ExtractedCaseData {
  title: string;
  caseNumber: string;
  courtType: string;
  district: string;
  stage: string;
  status: string;
  caseType: string;
  matterType: string;
  caseCategory: CaseCategoryLabel | string;
  parties: ExtractedCaseParty[];
  nextAction: string;
  nextHearingDate: string;
  documentType?: string;
  isCustomCase?: boolean;
  closestStandardArea?: string;
  caseSubtype?: string;
  caseMatter?: string;
  criminalCategory?: string;
  proceedingType?: string;
}

export interface ConvertedDocxImport {
  plainText: string;
  tabs: ImportTabDraft[];
  splitCanSplit: boolean;
  result: ImportDocxResult;
}

export interface CreateCaseFromImportOptions {
  existingDraftId?: string;
  draftTitle?: string;
  draftSource?: string;
  importStatus?: OldCaseImportStatus;
  inferredCategory?: string;
}

export interface CreateCaseFromImportResult {
  caseId: string;
  draftId: string;
}

function tabsWithSpacing(tabs: ImportTabDraft[]): DocumentTab[] {
  return tabs.map((tab) => ({
    ...tab,
    paragraphSpacing: DEFAULT_PARAGRAPH_SPACING_PT,
    lineSpacing: DEFAULT_LINE_SPACING,
  }));
}

export function titleFromFileName(name: string): string {
  return name.replace(/\.docx$/i, "").trim() || "Untitled Document";
}

export async function convertDocxForImport(file: File): Promise<ConvertedDocxImport> {
  const result = await importDocxFile(file, { mode: "preserve" });
  const layout = result.splitAnalysis.canSplit ? "tabs" : "single";
  const tabs = buildImportTabs(result, layout);
  return {
    plainText: result.plainText,
    tabs,
    splitCanSplit: result.splitAnalysis.canSplit,
    result,
  };
}

export function resolveMatterTypeFromExtracted(caseType?: string): string {
  const validCaseTypes = getAllMatterTypeIds();
  let matchedCaseType = "Civil Suit";
  if (caseType) {
    const finding = validCaseTypes.find(
      (t) => t.toLowerCase() === String(caseType).toLowerCase(),
    );
    if (finding) matchedCaseType = finding;
    else {
      const resolved = resolveMatterTypeId(caseType);
      if (validCaseTypes.includes(resolved)) matchedCaseType = resolved;
    }
  }
  return matchedCaseType;
}

export async function extractCaseFieldsFromDocx(
  plainText: string,
  options?: {
    defaultDistrict?: string;
    fallbackTitle?: string;
    maxChars?: number;
    signal?: AbortSignal;
  },
): Promise<ExtractedCaseData> {
  const trimmed = plainText.trim();
  if (!trimmed) {
    throw new Error("The document appears to be empty or contains no readable text.");
  }

  const excerpt =
    options?.maxChars && trimmed.length > options.maxChars
      ? trimmed.slice(0, options.maxChars)
      : trimmed;

  const parsedData = await extractCaseMetadataWithTimeout(excerpt, options?.signal);
  const matchedCaseType = resolveMatterTypeFromExtracted(parsedData.caseType);
  const caseCategory = inferCategoryFromMatterType(matchedCaseType) ?? "Civil";
  const startingStage = resolveStartingCaseStage(
    { caseCategory, matterType: matchedCaseType },
    "plaintiff",
  );

  return {
    title: parsedData.title || options?.fallbackTitle || "New Case from Document",
    caseNumber: parsedData.caseNumber || "",
    courtType: parsedData.courtType || "Unknown Court",
    district: parsedData.district || options?.defaultDistrict || "",
    stage: startingStage,
    status: "open",
    caseType: matchedCaseType,
    matterType: matchedCaseType,
    caseCategory,
    parties: Array.isArray(parsedData.parties) ? parsedData.parties : [],
    nextAction: parsedData.nextAction || "",
    nextHearingDate: parsedData.nextHearingDate || "",
  };
}

export async function getWorkspaceCaseCount(workspaceId: string): Promise<number> {
  const snap = await getDocs(
    query(collection(db, "workspaces", workspaceId, "cases"), limit(200)),
  );
  return snap.docs.filter((d) => !isDeleted(d.data())).length;
}

export async function createCaseFromImport(
  workspaceId: string,
  userId: string,
  extractedData: ExtractedCaseData,
  tabs: ImportTabDraft[],
  options?: CreateCaseFromImportOptions,
): Promise<CreateCaseFromImportResult> {
  const isCustomCase = Boolean(extractedData.isCustomCase);
  const matterType = isCustomCase
    ? extractedData.matterType?.trim() || extractedData.caseType?.trim() || "Custom Matter"
    : resolveMatterTypeId(extractedData.caseType || extractedData.matterType) || "Civil Suit";
  const caseCategory = isCustomCase
    ? extractedData.caseCategory || "Custom"
    : inferCategoryFromMatterType(matterType) ?? "Civil";
  const caseMatter = isCustomCase
    ? extractedData.caseMatter || extractedData.caseCategory || matterType
    : caseCategory;

  const caseRef = await addDoc(collection(db, "workspaces", workspaceId, "cases"), {
    ...extractedData,
    workspaceId,
    lawyerId: userId,
    assignedTo: [userId],
    representingSide: "plaintiff",
    caseMatter,
    matterType,
    caseType: matterType,
    caseCategory,
    ...(isCustomCase && {
      isCustomCase: true,
      closestStandardArea: extractedData.closestStandardArea || "",
      caseSubtype: extractedData.caseSubtype || "",
    }),
    district: extractedData.district?.trim() || "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: userId,
    visibility: "organization",
    ...privatePracticeCaseFields(),
  });

  const draftTitle =
    options?.draftTitle ||
    (extractedData.documentType
      ? `${extractedData.documentType} - ${extractedData.title}`
      : extractedData.title || "Imported Document");

  let draftId = options?.existingDraftId;

  if (draftId) {
    await updateDoc(doc(db, "workspaces", workspaceId, "documents", draftId), {
      caseId: caseRef.id,
      source: options?.draftSource || "docx_case_import",
      importStatus: options?.importStatus || "case_created",
      inferredCategory: options?.inferredCategory || caseCategory,
      updatedAt: serverTimestamp(),
    });
  } else {
    draftId = await createDraftDocument({
      title: draftTitle,
      tabs: tabsWithSpacing(tabs),
      activeTabId: tabs[0]?.id || "tab-1",
      documentType: extractedData.documentType || extractedData.caseType || "Other",
      caseId: caseRef.id,
      lawyerId: userId,
      workspaceId,
      source: options?.draftSource || "docx_case_import",
      importStatus: options?.importStatus || "case_created",
      inferredCategory: options?.inferredCategory || caseCategory,
    });
  }

  return { caseId: caseRef.id, draftId };
}

const AI_EXTRACT_TIMEOUT_MS = 90_000;

async function extractCaseMetadataWithTimeout(
  plainText: string,
  signal?: AbortSignal,
) {
  if (signal?.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(
      () =>
        reject(
          new Error(
            "AI extraction timed out. Check your connection and try again, or confirm VITE_API_BASE_URL is set.",
          ),
        ),
      AI_EXTRACT_TIMEOUT_MS,
    );
  });

  const abortPromise =
    signal &&
    new Promise<never>((_, reject) => {
      if (signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      signal.addEventListener(
        'abort',
        () => reject(new DOMException('Aborted', 'AbortError')),
        { once: true },
      );
    });

  try {
    return await Promise.race([
      extractCaseMetadataFromPlainText(plainText, signal),
      timeoutPromise,
      ...(abortPromise ? [abortPromise] : []),
    ]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

export async function createOldCaseImportDraft(
  workspaceId: string,
  userId: string,
  fileName: string,
  tabs: ImportTabDraft[],
  plainText?: string,
): Promise<string> {
  const title = titleFromFileName(fileName);
  const importTabs = tabsWithSpacing(tabs);
  const excerpt = plainText?.trim().slice(0, 50_000) ?? "";
  return createDraftDocument({
    title,
    tabs: importTabs,
    activeTabId: importTabs[0].id,
    lawyerId: userId,
    workspaceId,
    documentType: "Word upload",
    source: OLD_CASE_IMPORT_SOURCE,
    originalFileName: fileName,
    importStatus: "processing",
    importPlainText: excerpt || undefined,
  });
}

export async function markOldCaseImportDraftOnly(
  workspaceId: string,
  draftId: string,
): Promise<void> {
  await updateDoc(doc(db, "workspaces", workspaceId, "documents", draftId), {
    importStatus: "draft_only",
    updatedAt: serverTimestamp(),
  });
}

export async function markOldCaseImportReview(
  workspaceId: string,
  draftId: string,
): Promise<void> {
  await updateDoc(doc(db, "workspaces", workspaceId, "documents", draftId), {
    importStatus: "review",
    updatedAt: serverTimestamp(),
  });
}

export async function markOldCaseImportError(
  workspaceId: string,
  draftId: string,
  message: string,
): Promise<void> {
  await updateDoc(doc(db, "workspaces", workspaceId, "documents", draftId), {
    importStatus: "error",
    importError: message,
    updatedAt: serverTimestamp(),
  });
}

export async function linkDraftToCase(
  workspaceId: string,
  userId: string,
  draftId: string,
  caseId: string,
  documentName: string,
): Promise<void> {
  const displayName = documentName.trim() || "Imported document";

  await updateDoc(doc(db, "workspaces", workspaceId, "documents", draftId), {
    caseId,
    source: OLD_CASE_IMPORT_SOURCE,
    importStatus: "linked",
    updatedAt: serverTimestamp(),
  });

  await addDoc(collection(db, "workspaces", workspaceId, "documents"), {
    caseId,
    lawyerId: userId,
    workspaceId,
    name: displayName,
    title: displayName,
    type: "case_document",
    documentKind: "miscellaneous",
    filedBy: "us",
    category: "miscellaneous",
    linkedDraftId: draftId,
    createdAt: new Date().toISOString(),
  });
}

export function applyMatterTypeChange(
  data: ExtractedCaseData,
  matterTypeOrValue: string | MatterTypePickerValue,
): ExtractedCaseData {
  const value: MatterTypePickerValue =
    typeof matterTypeOrValue === "string"
      ? { mode: "standard", matterTypeId: matterTypeOrValue }
      : matterTypeOrValue;

  const { update } = buildMatterTypeUpdateFromPicker(value, data, {
    alwaysResetStage: true,
  });

  return {
    ...data,
    caseType: String(update.caseType ?? data.caseType),
    matterType: String(update.matterType ?? data.matterType),
    caseCategory: String(update.caseCategory ?? data.caseCategory),
    stage: String(update.stage ?? data.stage),
    isCustomCase: Boolean(update.isCustomCase),
    closestStandardArea: String(update.closestStandardArea ?? ""),
    caseSubtype: String(update.caseSubtype ?? ""),
    caseMatter: String(update.caseMatter ?? data.caseMatter ?? update.caseCategory),
    criminalCategory: update.criminalCategory as string | undefined,
    proceedingType: update.proceedingType as string | undefined,
  };
}
